import React, { useMemo, type FunctionComponent } from 'react'
import { SimpleSelect } from 'legacy/dash-components/simple-select/SimpleSelect'
import { useDashIntl } from 'intl/intl.hooks'
import { type DashMessages } from 'intl/intl.types'
import { type BoxProps } from 'legacy/dash-blocks/box/Box'
import { OrderSource } from 'legacy/sections/orders/utils/orders.types'
import { dashPixels } from 'legacy/dash-blocks/foundation/spacing-units/spacingUnits.types'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'

const ORDER_SOURCE_OPTIONS: { value: OrderSource | null; labelId: DashMessages }[] = [
  { value: null, labelId: 'filters.orderSource.all' },
  { value: OrderSource.Marketplace, labelId: 'filters.orderSource.marketplace' },
  { value: OrderSource.PBI, labelId: 'filters.orderSource.pbi' },
  { value: OrderSource.Unata, labelId: 'filters.orderSource.unata' },
  { value: OrderSource.API, labelId: 'filters.orderSource.api' },
]

interface Props extends BoxProps {
  value?: OrderSource
  onChange: (nextValue: OrderSource) => void
}

export const OrderSourceFilter: FunctionComponent<Props> = ({ value, ...rest }) => {
  const intl = useDashIntl()
  const warehouseContext = useWarehouseContext()

  const orderSourceOptions = useMemo(
    () =>
      ORDER_SOURCE_OPTIONS.map(n => ({
        value: n.value,
        label: intl.formatMessage({ id: n.labelId }),
      })),
    [intl]
  )

  const filteredOptions = useMemo(() => {
    if (!warehouseContext.loaded) {
      return []
    }

    const { orderSources } = warehouseContext.warehouse

    if (!orderSources) {
      return []
    }

    return orderSourceOptions.filter(({ value: orderSourceValue }) => {
      // "All Sources" Option
      if (orderSourceValue === null) return true

      return orderSources.includes(orderSourceValue)
    })
  }, [orderSourceOptions, warehouseContext])

  // At least two choices [aside from "All"] are available
  const shouldShowOrderSourceFilter = filteredOptions.length > 2

  if (!shouldShowOrderSourceFilter) {
    return null
  }

  return (
    <SimpleSelect<OrderSource | null>
      width={dashPixels.PX160}
      defaultValue={null}
      options={filteredOptions}
      value={value || null}
      {...rest}
    />
  )
}
